import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import ConfirmModal from '../components/ConfirmModal';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { vendorAPI, authAPI, adminAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiUser, FiLock, FiInfo, FiMail, FiPhone, FiBriefcase, FiSliders, FiPlus, FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight, FiAlertTriangle, FiShield, FiGrid } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { formatAmount } from '../utils/currency';
import { useTone } from '../contexts/ToneContext';
import TwoFactorSettings from '../components/TwoFactorSettings';
import './Pages.css';

const VENDOR_TRACKING_KEY = 'hisab_vendor_tracking_enabled';
const DETAILED_VIEW_KEY = 'hisab_detailed_txn_view';

const getVendorTrackingEnabled = () => {
  return localStorage.getItem(VENDOR_TRACKING_KEY) === 'true';
};

const getDetailedViewEnabled = () => {
  return localStorage.getItem(DETAILED_VIEW_KEY) === 'true';
};

const ResetAccountSection = () => {
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const handleResetAccount = async () => {
    if (!resetPassword) {
      toast.error('Please enter your password to confirm.');
      return;
    }
    setResetting(true);
    try {
      await authAPI.resetAccount({ password: resetPassword });
      toast.success('Account reset complete. All data has been deleted and default ledgers re-created.');
      setShowResetConfirm(false);
      setResetPassword('');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset account.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="settings-danger-zone" style={{ marginTop: 0 }}>
      <h3><FiAlertTriangle style={{ marginRight: 6, verticalAlign: 'middle' }} />Reset Account</h3>
      <p style={{ marginBottom: 8 }}>
        <strong>This action is irreversible.</strong> It will permanently delete:
      </p>
      <ul style={{ color: '#7F8C8D', fontSize: '0.9rem', paddingLeft: 20, marginBottom: 16 }}>
        <li>All ledgers and their balances</li>
        <li>All transactions (income, expenses, payables)</li>
        <li>All reminders and transfers</li>
        <li>All vendor records and budgets</li>
      </ul>
      <p style={{ color: '#7F8C8D', fontSize: '0.9rem', marginBottom: 16 }}>
        Default ledgers will be re-created. Your account and login will remain.
      </p>

      {!showResetConfirm ? (
        <button className="btn btn-danger" onClick={() => setShowResetConfirm(true)}>
          Reset All Data
        </button>
      ) : (
        <div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label style={{ color: '#E74C3C', fontWeight: 600 }}>Enter your password to confirm:</label>
            <input
              type="password"
              value={resetPassword}
              onChange={e => setResetPassword(e.target.value)}
              placeholder="Your current password"
              autoFocus
            />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-danger" onClick={handleResetAccount} disabled={resetting}>
              {resetting ? 'Resetting...' : 'Confirm Reset'}
            </button>
            <button className="btn btn-secondary" onClick={() => { setShowResetConfirm(false); setResetPassword(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Settings = () => {
  const { user, updateUser, logout } = useAuth();
  const { toneEnabled, toggleTone } = useTone();
  const { theme, isDark, setTheme, toggleDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    business_name: user?.business_name || '',
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [saving, setSaving] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);

  // View preference state
  const [detailedViewEnabled, setDetailedViewEnabled] = useState(getDetailedViewEnabled);
  // Vendor tracking state
  const [vendorTrackingEnabled, setVendorTrackingEnabled] = useState(getVendorTrackingEnabled);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [vendorForm, setVendorForm] = useState({ name: '', phone: '', notes: '' });
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [deleteVendorTarget, setDeleteVendorTarget] = useState(null);

  useEffect(() => {
    adminAPI.getMyPlan().then(res => {
      setPlanInfo(res.data.data.plan);
    }).catch(() => {
      setPlanInfo({ plan_name: 'basic', display_name: 'Basic (Free)', features: [] });
    });
  }, []);

  useEffect(() => {
    localStorage.setItem(VENDOR_TRACKING_KEY, vendorTrackingEnabled.toString());
  }, [vendorTrackingEnabled]);

  useEffect(() => {
    if (vendorTrackingEnabled && activeTab === 'preferences') {
      fetchVendors();
    }
  }, [vendorTrackingEnabled, activeTab]);

  const fetchVendors = async () => {
    setVendorsLoading(true);
    try {
      const res = await vendorAPI.getAll();
      setVendors(res.data.data.vendors || []);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setVendorsLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateUser(profileData);
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    toast.info('Password change will be available in the next update');
    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
  };

  // Vendor handlers
  const toggleVendorTracking = () => {
    const newValue = !vendorTrackingEnabled;
    setVendorTrackingEnabled(newValue);
    toast.success(newValue ? 'Vendor tracking enabled' : 'Vendor tracking disabled');
  };

  const resetVendorForm = () => {
    setVendorForm({ name: '', phone: '', notes: '' });
    setEditingVendorId(null);
  };

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    if (!vendorForm.name.trim()) {
      toast.error('Vendor name is required');
      return;
    }

    try {
      if (editingVendorId) {
        await vendorAPI.update(editingVendorId, {
          name: vendorForm.name.trim(),
          phone: vendorForm.phone.trim() || undefined,
          notes: vendorForm.notes.trim() || undefined,
        });
        toast.success('Vendor updated');
      } else {
        await vendorAPI.create({
          name: vendorForm.name.trim(),
          phone: vendorForm.phone.trim() || undefined,
          notes: vendorForm.notes.trim() || undefined,
        });
        toast.success('Vendor added');
      }

      setShowVendorModal(false);
      resetVendorForm();
      fetchVendors();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('A vendor with this name already exists');
      } else {
        toast.error(error.response?.data?.message || 'Operation failed');
      }
    }
  };

  const handleEditVendor = (vendor) => {
    setVendorForm({ name: vendor.name, phone: vendor.phone || '', notes: vendor.notes || '' });
    setEditingVendorId(vendor.id);
    setShowVendorModal(true);
  };

  const handleDeleteVendor = async () => {
    if (!deleteVendorTarget) return;
    try {
      await vendorAPI.delete(deleteVendorTarget);
      toast.success('Vendor removed');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to remove vendor');
    } finally {
      setDeleteVendorTarget(null);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: <FiUser /> },
    { id: 'preferences', label: 'Preferences', icon: <FiSliders /> },
    { id: 'plan', label: 'My Plan', icon: <FiShield /> },
    { id: 'security', label: 'Security', icon: <FiLock /> },
    { id: 'about', label: 'About', icon: <FiInfo /> },
  ];

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <h1>Settings</h1>
        </div>

        <div className="settings-layout">
          <div className="settings-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="settings-content">
            {activeTab === 'profile' && (
              <div className="card">
                <h2><FiUser style={{ marginRight: 8, verticalAlign: 'middle' }} />Profile Information</h2>

                <div className="settings-info-row">
                  <FiMail />
                  <div>
                    <span className="settings-label">Email</span>
                    <span className="settings-value">{user?.email}</span>
                  </div>
                </div>

                <div className="settings-info-row">
                  <FiInfo />
                  <div>
                    <span className="settings-label">Member since</span>
                    <span className="settings-value">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', {
                        year: 'numeric', month: 'long', day: 'numeric'
                      }) : 'N/A'}
                    </span>
                  </div>
                </div>

                <hr className="settings-divider" />

                <form onSubmit={handleProfileSubmit}>
                  <div className="form-group">
                    <label><FiUser style={{ marginRight: 6, verticalAlign: 'middle' }} />Full Name</label>
                    <input
                      type="text"
                      name="full_name"
                      value={profileData.full_name}
                      onChange={handleProfileChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label><FiPhone style={{ marginRight: 6, verticalAlign: 'middle' }} />Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={profileData.phone}
                      onChange={handleProfileChange}
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div className="form-group">
                    <label><FiBriefcase style={{ marginRight: 6, verticalAlign: 'middle' }} />Business Name</label>
                    <input
                      type="text"
                      name="business_name"
                      value={profileData.business_name}
                      onChange={handleProfileChange}
                      placeholder="Enter business name"
                    />
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="card">
                <h2><FiSliders style={{ marginRight: 8, verticalAlign: 'middle' }} />Preferences</h2>

                <div className="preference-item">
                  <div className="preference-info">
                    <h4>Manage Categories</h4>
                    <p className="preference-desc">
                      Create, edit, and bookmark your expense and income categories.
                    </p>
                  </div>
                  <Link to="/categories" className="btn btn-primary btn-sm">
                    <FiGrid style={{ marginRight: 4 }} />
                    Open Categories
                  </Link>
                </div>

                <hr className="settings-divider" />

                <div className="preference-item">
                  <div className="preference-info">
                    <h4>Hisab Okay Hai Tone</h4>
                    <p className="preference-desc">
                      Use friendly, everyday Hindi words instead of formal finance terms.
                    </p>
                  </div>
                  <button
                    className={`toggle-btn ${toneEnabled ? 'on' : 'off'}`}
                    onClick={toggleTone}
                    title={toneEnabled ? 'Switch to English labels' : 'Switch to Hisab tone'}
                  >
                    {toneEnabled ? <FiToggleRight size={28} /> : <FiToggleLeft size={28} />}
                  </button>
                </div>

                <hr className="settings-divider" />

                <div className="preference-item">
                  <div className="preference-info">
                    <h4>Use Detailed Transaction View</h4>
                    <p className="preference-desc">
                      Show full transaction details with type badges and dates. When off, a modern compact view is used.
                    </p>
                  </div>
                  <button
                    className={`toggle-btn ${detailedViewEnabled ? 'on' : 'off'}`}
                    onClick={() => {
                      const next = !detailedViewEnabled;
                      setDetailedViewEnabled(next);
                      localStorage.setItem(DETAILED_VIEW_KEY, next.toString());
                      toast.success(next ? 'Detailed view enabled' : 'Modern view enabled');
                    }}
                    title={detailedViewEnabled ? 'Switch to modern view' : 'Switch to detailed view'}
                  >
                    {detailedViewEnabled ? <FiToggleRight size={28} /> : <FiToggleLeft size={28} />}
                  </button>
                </div>

                <hr className="settings-divider" />

                <div className="preference-item">
                  <div className="preference-info">
                    <h4>Theme</h4>
                    <p className="preference-desc">
                      Choose between classic and modern visual style.
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      className={`toggle-btn ${theme === 'classic' ? 'on' : 'off'}`}
                      onClick={() => { setTheme('classic'); toast.success('Classic theme applied'); }}
                      style={{ fontSize: '0.78rem', padding: '4px 12px', borderRadius: 8 }}
                    >
                      Classic
                    </button>
                    <button
                      className={`toggle-btn ${theme === 'modern' ? 'on' : 'off'}`}
                      onClick={() => { setTheme('modern'); toast.success('Modern theme applied'); }}
                      style={{ fontSize: '0.78rem', padding: '4px 12px', borderRadius: 8 }}
                    >
                      Modern
                    </button>
                  </div>
                </div>

                <hr className="settings-divider" />

                <div className="preference-item">
                  <div className="preference-info">
                    <h4>Dark Mode</h4>
                    <p className="preference-desc">
                      Enable dark colors across the entire app. Works with both themes.
                    </p>
                  </div>
                  <button
                    className={`toggle-btn ${isDark ? 'on' : 'off'}`}
                    onClick={() => { toggleDarkMode(); toast.success(isDark ? 'Light mode enabled' : 'Dark mode enabled'); }}
                    title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  >
                    {isDark ? <FiToggleRight size={28} /> : <FiToggleLeft size={28} />}
                  </button>
                </div>

                <hr className="settings-divider" />

                <div className="preference-item">
                  <div className="preference-info">
                    <h4>Vendor Tracking</h4>
                    <p className="preference-desc">
                      Track payments to specific vendors. When enabled, a vendor selector
                      will appear when adding expenses.
                    </p>
                  </div>
                  <button
                    className={`toggle-btn ${vendorTrackingEnabled ? 'on' : 'off'}`}
                    onClick={toggleVendorTracking}
                    title={vendorTrackingEnabled ? 'Disable vendor tracking' : 'Enable vendor tracking'}
                  >
                    {vendorTrackingEnabled ? <FiToggleRight size={28} /> : <FiToggleLeft size={28} />}
                  </button>
                </div>

                {vendorTrackingEnabled && (
                  <>
                    <hr className="settings-divider" />

                    <div className="vendor-section">
                      <div className="vendor-section-header">
                        <h3>Manage Vendors</h3>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => { resetVendorForm(); setShowVendorModal(true); }}
                        >
                          <FiPlus style={{ marginRight: 4, verticalAlign: 'middle' }} />
                          Add Vendor
                        </button>
                      </div>

                      {vendorsLoading ? (
                        <p style={{ color: '#999' }}>Loading vendors...</p>
                      ) : vendors.length === 0 ? (
                        <p className="vendor-empty">No vendors added yet. Add your first vendor to start tracking payments.</p>
                      ) : (
                        <div className="vendor-list">
                          {vendors.map(vendor => (
                            <div key={vendor.id} className="vendor-item">
                              <div className="vendor-info">
                                <span className="vendor-name">{vendor.name}</span>
                                {vendor.phone && (
                                  <span className="vendor-desc">{vendor.phone}</span>
                                )}
                                {vendor.notes && (
                                  <span className="vendor-desc">{vendor.notes}</span>
                                )}
                                {(parseFloat(vendor.total_billed) > 0 || parseFloat(vendor.total_paid) > 0) && (
                                  <span className="vendor-desc" style={{ fontSize: '0.8em', color: '#7f8c8d' }}>
                                    Billed: {formatAmount(vendor.total_billed, 0)} | Paid: {formatAmount(vendor.total_paid, 0)}
                                  </span>
                                )}
                              </div>
                              <div className="vendor-actions">
                                <button
                                  className="btn-icon"
                                  onClick={() => handleEditVendor(vendor)}
                                  title="Edit"
                                >
                                  <FiEdit2 />
                                </button>
                                <button
                                  className="btn-icon danger"
                                  onClick={() => setDeleteVendorTarget(vendor.id)}
                                  title="Remove"
                                >
                                  <FiTrash2 />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'plan' && (
              <div className="card">
                <h2><FiShield style={{ marginRight: 8, verticalAlign: 'middle' }} />My Subscription Plan</h2>

                {planInfo ? (
                  <div className="plan-info-card">
                    <h4>{planInfo.display_name || planInfo.plan_name}</h4>
                    <p style={{ color: '#7F8C8D', margin: '4px 0 0', fontSize: '0.9rem' }}>
                      Status: {planInfo.status || 'Active'}
                      {planInfo.expires_at && ` | Expires: ${new Date(planInfo.expires_at).toLocaleDateString('en-IN')}`}
                    </p>

                    {planInfo.features && planInfo.features.length > 0 && planInfo.features[0]?.feature_key && (
                      <div className="plan-info-features">
                        <h4 style={{ marginTop: 16 }}>Plan Features</h4>
                        {planInfo.features.map((f, idx) => (
                          <span key={idx} className={f.is_enabled ? 'enabled' : 'disabled'}>
                            {f.is_enabled ? '+ ' : '- '}{f.feature_label}
                            {f.limit_value ? ` (limit: ${f.limit_value})` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ color: '#999' }}>Loading plan info...</p>
                )}

                <p style={{ color: '#95A5A6', fontSize: '0.85rem', marginTop: 16 }}>
                  To upgrade your plan, contact support or ask your admin.
                </p>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="card">
                <h2><FiLock style={{ marginRight: 8, verticalAlign: 'middle' }} />Security</h2>

                {/* Two-Factor Authentication */}
                <TwoFactorSettings />

                <hr className="settings-divider" />

                {/* Change Password */}
                <h3 style={{ marginTop: 0 }}>Change Password</h3>
                <form onSubmit={handlePasswordSubmit}>
                  <div className="form-group">
                    <label>Current Password</label>
                    <input
                      type="password"
                      name="current_password"
                      value={passwordData.current_password}
                      onChange={handlePasswordChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      name="new_password"
                      value={passwordData.new_password}
                      onChange={handlePasswordChange}
                      required
                      minLength={6}
                    />
                  </div>

                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      name="confirm_password"
                      value={passwordData.confirm_password}
                      onChange={handlePasswordChange}
                      required
                    />
                  </div>

                  <button type="submit" className="btn btn-primary">
                    Change Password
                  </button>
                </form>

                <hr className="settings-divider" />

                <div className="settings-danger-zone">
                  <h3>Danger Zone</h3>
                  <p>Signing out will clear your session on this device.</p>
                  <button className="btn btn-danger" onClick={logout}>
                    Sign Out
                  </button>
                </div>

                <hr className="settings-divider" />

                <ResetAccountSection />
              </div>
            )}

            {activeTab === 'about' && (
              <div className="card">
                <h2><FiInfo style={{ marginRight: 8, verticalAlign: 'middle' }} />About Hisab OK Hai</h2>

                <div className="about-section">
                  <div className="about-logo">
                    <h3>Hisab OK Hai</h3>
                    <p className="about-tagline">Roz ka kharcha, bina tension</p>
                  </div>

                  <div className="about-details">
                    <div className="about-item">
                      <span className="about-label">Version</span>
                      <span className="about-value">1.0.0</span>
                    </div>
                    <div className="about-item">
                      <span className="about-label">Platform</span>
                      <span className="about-value">Web</span>
                    </div>
                    <div className="about-item">
                      <span className="about-label">Developer</span>
                      <span className="about-value">{process.env.REACT_APP_BUSINESS_NAME || 'INDIATECH247'}</span>
                    </div>
                    <div className="about-item">
                      <span className="about-label">Contact</span>
                      <span className="about-value">{process.env.REACT_APP_BUSINESS_PHONE || '99115 20247'}</span>
                    </div>
                  </div>

                  <div className="about-features">
                    <h4>What you can do</h4>
                    <ul>
                      <li>Track income and expenses across multiple ledgers</li>
                      <li>Set budgets and monitor spending</li>
                      <li>Get reminders for upcoming payments</li>
                      <li>View reports and spending insights</li>
                      <li>Upload and manage receipts</li>
                      <li>Track vendor payments and balances</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {showVendorModal && (
          <div className="modal" onClick={() => { setShowVendorModal(false); resetVendorForm(); }}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h2>{editingVendorId ? 'Edit Vendor' : 'Add Vendor'}</h2>
              <form onSubmit={handleVendorSubmit}>
                <div className="form-group">
                  <label>Vendor Name</label>
                  <input
                    type="text"
                    value={vendorForm.name}
                    onChange={e => setVendorForm({ ...vendorForm, name: e.target.value })}
                    placeholder="e.g. Electricity Board, Milkman, Netflix"
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Phone (optional)</label>
                  <input
                    type="tel"
                    value={vendorForm.phone}
                    onChange={e => setVendorForm({ ...vendorForm, phone: e.target.value })}
                    placeholder="e.g. 9876543210"
                  />
                </div>
                <div className="form-group">
                  <label>Notes (optional)</label>
                  <textarea
                    value={vendorForm.notes}
                    onChange={e => setVendorForm({ ...vendorForm, notes: e.target.value })}
                    placeholder="e.g. Monthly electricity bill"
                    rows="2"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowVendorModal(false); resetVendorForm(); }}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingVendorId ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {deleteVendorTarget && (
          <ConfirmModal
            title="Remove Vendor"
            message="Are you sure you want to remove this vendor? This will not delete any past transactions."
            confirmLabel="Remove"
            onConfirm={handleDeleteVendor}
            onCancel={() => setDeleteVendorTarget(null)}
          />
        )}
      </div>
    </Layout>
  );
};

export default Settings;
